
import { NewFolderDialog } from "../../dialogs/NewFolderDialog";
import { NewContainerDialog, ValidationErrors } from "../../dialogs/NewContainerDialog";
import { CreateContainerDto } from "../../dialogs/types/NewContainerTypes";
import { ItemsApiProvider } from "../../providers/ItemsApiProvider";
import { ContainersApiProvider } from "../../providers/ContainersApiProvider";
import { ApiError } from "../../types/ApiError";
import { isContainerCreator } from "../../utils/AuthUtils";
import { ContainerDropdown } from "../container-dropdown/ContainerDropdown";
import {
	Button,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	MenuPopover,
	MenuTrigger,
	Spinner,
	Breadcrumb,
	BreadcrumbButton,
	BreadcrumbItem,
	BreadcrumbDivider,
	Skeleton,
	SkeletonItem,
	Tooltip
} from '@fluentui/react-components';
import {
	ArrowUpload20Filled,
	Add20Filled,
	Folder24Regular,
    BoxMultiple20Regular
} from "@fluentui/react-icons";
import React, { useRef, useState } from "react";
import { ContainerSearchItems } from "../container-search-items/ContainerSearchItems";
import { IContainerActionBarProps } from "./types/IContainerActionBarProps";
import { IPendingUpload } from "./types/IPendingUpload";
import { useNavigate } from "react-router";
import { UploadErrorDialog } from "../drag-drop/UploadErrorDialog";
import { ErrorDialog } from "../ErrorDialog";
import { handleUploadErrors, UploadError } from "../../utils/uploadErrorHandler";
import './ContainerActionBar.css';
import { useBlacklistedExtensionsContext } from "../../common/BlacklistedExtensions";
import ContactKaiaFormText from "../contact-kaia-form-text/ContactKaiaFormText";
import { useContainerActionBarStyles } from "./ContainerActionBar.styles";
import { useSharedStyles } from "../../shared.styles";
import { isInShell } from "../../common/Shell";
import { isInTeams } from "../../common/Teams";

const itemsApi = ItemsApiProvider.instance;
const containersApi = ContainersApiProvider.instance;

export const ContainerActionBar: React.FunctionComponent<IContainerActionBarProps> = (props: IContainerActionBarProps) => {
	const [uploads, setUploads] = useState<Map<string, IPendingUpload>>(new Map<string, IPendingUpload>());
	const uploadFileRef = useRef<HTMLInputElement>(null);
	const [showCreatingSpinner, setShowCreatingSpinner] = useState<boolean>(false);
	const [showNewFolderDialog, setShowNewFolderDialog] = useState<boolean>(false);
	const [showNewContainerDialog, setShowNewContainerDialog] = useState<boolean>(false);
	const [containerValidationErrors, setContainerValidationErrors] = useState<ValidationErrors | undefined>(undefined);
	const [containerCreatedSuccessfully, setContainerCreatedSuccessfully] = useState<boolean>(false);
	const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [errorDetails, setErrorDetails] = useState<string>('');
	const [showGenericErrorDialog, setShowGenericErrorDialog] = useState<boolean>(false);
	const [genericErrorMessage, setGenericErrorMessage] = useState<string>('');
	const [genericErrorDetails, setGenericErrorDetails] = useState<string>('');
	const navigate = useNavigate();
	const actionBarStyles = useContainerActionBarStyles();
	const sharedStyles = useSharedStyles();

	// Check two separate conditions that should block create functionality:
	// 1. When in the "All" container at root level (read-only aggregated view)
	// 2. When searching in ANY container (search results are read-only)
	const isAllContainer = props.startingContainerId?.toLowerCase() === "all";
	const isSearching = Boolean(props.searchQuery && props.searchQuery.trim().length > 0);
	const isAllContainerRoot = isAllContainer && props.isContainerRoot;
	const shouldBlockCreate = isAllContainerRoot || isSearching;
	const inShell = React.useMemo(() => isInShell(), []);
	const inTeams = React.useMemo(() => isInTeams(), []);
	const userIsContainerCreator = React.useMemo(() => isContainerCreator(), []);

	const showUploadError = (message: string, details?: string) => {
		setErrorMessage(message);
		setErrorDetails(details || '');
		setShowErrorDialog(true);
		// Don't call props.onUploadError here to avoid circular calls
		// The error dialog will be shown by this component
	};

	const showGenericError = (message: string, details?: string) => {
		setGenericErrorMessage(message);
		setGenericErrorDetails(details || '');
		setShowGenericErrorDialog(true);
	};

	// Expose the error handler to parent component via ref
	React.useEffect(() => {
		if (props.errorHandlerRef) {
			props.errorHandlerRef.current = showUploadError;
		}
	}, [props.errorHandlerRef]);

	const handleErrorDialogDismiss = () => {
		setShowErrorDialog(false);
		setErrorMessage('');
		setErrorDetails('');
	};

	const createNewFolder = async (folderName: string) => {
		setShowCreatingSpinner(true);
		try {
			await itemsApi.createFolder(props.containerId, props.parentId, folderName);
			props.onItemsUpdated?.();
		} catch (err) {
			const errorMessage = `Unable to create folder "${folderName}"`;
			const errorDetails = err instanceof Error ? err.message : 'Please check your permissions or try again later';
			showGenericError(errorMessage, errorDetails);
		} finally {
			setShowCreatingSpinner(false);
			setShowNewFolderDialog(false);
		}
	};

	const createNewContainer = async (createContainerDTO: CreateContainerDto) => {
		setShowCreatingSpinner(true);
		setContainerValidationErrors(undefined); // Clear previous errors
		setContainerCreatedSuccessfully(false); // Clear previous success state
		try {
			await containersApi.create(createContainerDTO);
			props.onItemsUpdated?.();
			setContainerCreatedSuccessfully(true);
			// Close dialog after showing success message
			setTimeout(() => {
				setShowNewContainerDialog(false);
				setContainerCreatedSuccessfully(false);
			}, 2000);
		} catch (err: unknown) {
			// Check if it's a validation error (400 with errors object)
			if (err instanceof ApiError && err.status === 400 && err.errorResponse?.errors) {
				setContainerValidationErrors(err.errorResponse.errors as ValidationErrors);
				return; // Keep dialog open
			}
			const errorMessage = `Unable to create container "${createContainerDTO.name}"`;
			const errorDetails = err instanceof Error ? err.message : 'Please check your permissions or try again later';
			showGenericError(errorMessage, errorDetails);
			setShowNewContainerDialog(false);
		} finally {
			setShowCreatingSpinner(false);
		}
	};

	const onUploadFileClick = () => {
		if (uploadFileRef.current) {
			uploadFileRef.current.click();
		}
	};

	const onUploadFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files || files.length === 0) {
			return;
		}

		const uploadErrors: UploadError[] = [];
		const uploadPromises: Promise<void>[] = [];
		let successfulUploads = 0;
		try {
			for (const file of Array.from(files)) {
				const upload: IPendingUpload = {
					containerId: props.containerId,
					parentId: props.parentId,
					file: file,
					uploadTask: itemsApi.createFile(props.containerId, props.parentId, file, blacklistedExtensions)
				};
				const uploadId = `${upload.containerId}/${upload.parentId}/${file.name}`;
				uploads.set(uploadId, upload);

				const uploadPromise = upload.uploadTask
					.then(() => {
						uploads.delete(uploadId);
						setUploads(new Map<string, IPendingUpload>(uploads));
						successfulUploads++;
					})
					.catch((err) => {
						console.error("Error uploading file:", err);
						uploads.delete(uploadId);
						setUploads(new Map<string, IPendingUpload>(uploads));
						uploadErrors.push({
							fileName: file.name,
							error: err instanceof Error ? err.message : 'Please check your permissions or try again later'
						});
					});

				uploadPromises.push(uploadPromise);
			}
			setUploads(new Map<string, IPendingUpload>(uploads));

			// Wait for all uploads to complete
			await Promise.allSettled(uploadPromises);

			// Refresh the list if any uploads succeeded, regardless of errors
			if (successfulUploads > 0) {
				props.onItemsUpdated?.();
			}

			handleUploadErrors(uploadErrors, showUploadError);
		} catch (err) {
			const errorMessage = err instanceof Error ? err.message : "Failed to upload file(s)";
			const errorDetails = err instanceof Error ? '' : 'Please check your permissions or try again later';
			showUploadError(errorMessage, errorDetails);
		} finally {
			// Reset the file input so the same file can be uploaded again
			if (event.target) {
				event.target.value = '';
			}
		}
	};

	const onSearchQueryChange = async (query: string) => {
		props.onSearchQueryChange(query);
		props.onSearchResults([]);
	}

	const handleContainerChange = (containerId: string) => {
		if (props.onContainerChange) {
			props.onContainerChange(containerId);
		} else {
			navigate(`/containers/${containerId}`);
		}
	};

	const blacklistedExtensions = useBlacklistedExtensionsContext();
	const blacklistedEtensionsText = blacklistedExtensions.length ? `Disallowed extensions: ${blacklistedExtensions.join(", ")} .` : '';

	if (props.isUILoading) {
		return (
			<div className="actionBarContainer">
				<div className="leftContent">
					{!props.isContainerRoot ? (
						<div>
							<Skeleton width="100%" style={{ height: '24px' }}>
								<SkeletonItem />
							</Skeleton>
						</div>
					) : (
						<div>
							<Skeleton width="100%" style={{ height: '30px' }}>
								<SkeletonItem />
							</Skeleton>
						</div>
					)}

					<div>
						<Skeleton width="100%" style={{ height: '30px' }}>
							<SkeletonItem />
						</Skeleton>
					</div>
				</div>

				<div>
					<Skeleton width="100%" style={{ height: '32px' }}>
						<SkeletonItem />
					</Skeleton>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="actionBarContainer">
				<div className="leftContent">
					{!props.isContainerRoot && (
						<div className="breadcrumbContainer">
							<Breadcrumb size="medium">
								{props.containerId && (
									<>
										<BreadcrumbItem className="breadcrumbItemHover breadcrumbItem">
											<BreadcrumbButton
												size="medium"
												onClick={props.onBackButtonClick}
												title={props.containers?.find(container => container.id === props.startingContainerId)?.name || props.containerId}
											>
												{props.containers?.find(container => container.id === props.startingContainerId)?.name || props.containerId}
											</BreadcrumbButton>
										</BreadcrumbItem>
										{props.folderPath.map((folder) => (
											<React.Fragment key={folder.id}>
												<BreadcrumbDivider>
												</BreadcrumbDivider>
												<BreadcrumbItem className="breadcrumbItemHover breadcrumbItem">
													<BreadcrumbButton
														onClick={() => props.onBreadcrumbClick?.(folder)}
														title={folder.name}
													>
														{folder.name}
													</BreadcrumbButton>
												</BreadcrumbItem>
											</React.Fragment>
										))}
									</>
								)}
							</Breadcrumb>
						</div>
					)}

					{props.isContainerRoot && props.containers && props.containers.length > 0 && (
						<ContainerDropdown
							selectedContainerId={props.containerId}
							onContainerChange={handleContainerChange}
							initialContainers={props.containers}
						/>
					)}

					<ContainerSearchItems
						containerId={props.containerId}
						isContainerRoot={props.isContainerRoot}
						searchQuery={props.searchQuery}
						onSearchResults={props.onSearchResults}
						onSearchQueryChange={onSearchQueryChange}
						page={props.page}
						pageSize={props.pageSize}
						setPage={props.setPage}
						setPageSize={props.setPageSize}
						setTotalResults={props.setTotalResults}
						setIsLoading={props.setIsLoading}
						isUILoading={props.isUILoading}
					/>
					<ContactKaiaFormText />
				</div>

				<div className="rightContent">
					<Menu>
						<MenuTrigger disableButtonEnhancement>
						<MenuButton
							icon={<Add20Filled />}
							appearance="secondary"
							size="medium"
							disabled={shouldBlockCreate}
							className={actionBarStyles.createButton}>
							Create
						</MenuButton>
						</MenuTrigger>
						<MenuPopover>
							<MenuList>
								{(!inShell && !inTeams && userIsContainerCreator) && (
									<MenuItem icon={<BoxMultiple20Regular className={sharedStyles.itemIcon} />} onClick={() => setShowNewContainerDialog(true)}>
										Container
									</MenuItem>
								)}
								<MenuItem icon={<Folder24Regular className={sharedStyles.itemIcon} />} onClick={() => setShowNewFolderDialog(true)}>
									Folder
								</MenuItem>
								{blacklistedEtensionsText ? (
									<Tooltip
										content={blacklistedEtensionsText}
										positioning={"before"}
										withArrow
										relationship="label"
									>
										<MenuItem icon={<ArrowUpload20Filled />} onClick={onUploadFileClick}>
											<>Upload</>
										</MenuItem>
									</Tooltip>
								) : (
									<MenuItem icon={<ArrowUpload20Filled />} onClick={onUploadFileClick}>
										<>Upload</>
									</MenuItem>
								)}
							</MenuList>
						</MenuPopover>
					</Menu>

					<input ref={uploadFileRef} type="file" multiple onChange={onUploadFileSelected} className="hiddenFileInput" />

					{(uploads.size > 0 || (props.dragDropUploads && props.dragDropUploads.size > 0)) && (
						<Button disabled>
							{uploads.size + (props.dragDropUploads?.size || 0)} files uploading
							<Spinner size="extra-tiny" />
						</Button>
					)}

					{showNewFolderDialog && (
						<NewFolderDialog
							showNewFolderDialog={showNewFolderDialog}
							showCreatingSpinner={showCreatingSpinner}
							onItemsUpdated={() => props.onItemsUpdated?.()}
							createNewFolder={createNewFolder}
							closeNewFolderModal={() => setShowNewFolderDialog(false)}
						/>
					)}

				{showNewContainerDialog && (
					<NewContainerDialog
						showNewContainerDialog={showNewContainerDialog}
						showCreatingSpinner={showCreatingSpinner}
						validationErrors={containerValidationErrors}
						createdSuccessfully={containerCreatedSuccessfully}
						onItemsUpdated={() => props.onItemsUpdated?.()}
						createNewContainer={createNewContainer}
						closeNewContainerModal={() => {
							setShowNewContainerDialog(false);
							setContainerValidationErrors(undefined);
							setContainerCreatedSuccessfully(false);
						}}
					/>
					)}
				</div>
			</div>
			<UploadErrorDialog
				isOpen={showErrorDialog}
				onDismiss={handleErrorDialogDismiss}
				message={errorMessage}
				details={errorDetails}
			/>
			<ErrorDialog
				isOpen={showGenericErrorDialog}
				onDismiss={() => setShowGenericErrorDialog(false)}
				message={genericErrorMessage}
				details={genericErrorDetails}
			/>
		</>
	);
};

export default ContainerActionBar;